// Mandar creaciones al calendario de Instagram desde el chat —
// ADR-2026-10-13-mandar-a-instagram-desde-el-chat.
//
// El calendario ya le da al agente sus herramientas (`crear_posts` y compañía), pero son de
// PANTALLA: viajan sólo con `/instagram` abierto. Ésta es la contracara, como `ir_al_estudio`
// para el Estudio: viaja cuando el calendario NO está abierto, y arma UN post con lo que se
// acaba de generar sin que el usuario cambie de pantalla.
//
// Es silenciosa (como `agregar_al_moodboard`): el post no se publica, queda planificado y
// se edita o borra desde el calendario, así que una tarjeta de confirmación sólo agregaría
// un clic. La respuesta cuelga en el mensaje un botón para ir a verlo.
//
// Lo puro (validar el pedido, elegir la hora, armar lo que lee el modelo) vive acá con sus
// dependencias inyectadas, para poder testearlo; el `ChatAtlasContext` le pasa las de verdad,
// que son los MISMOS servicios que usa el calendario.

import type { HerramientaChat } from './tipos';
import type { PostEnAgenda, Sugerencia } from '../pages/lanzamientos/redes/mejorMomento';

export const NOMBRE_MANDAR_A_INSTAGRAM = 'mandar_a_instagram';

/** Tope de piezas de un post de Instagram (carrusel). Mismo número que `MAX_PIEZAS`. */
const MAX_PIEZAS_POST = 10;
/** Tope del texto de un post. Mismo número que `MAX_TEXTO`. */
const MAX_TEXTO_POST = 2200;

export const HERRAMIENTA_MANDAR_A_INSTAGRAM: HerramientaChat = {
  name: NOMBRE_MANDAR_A_INSTAGRAM,
  description:
    'Agrega UN post al calendario de Instagram de la marca con creaciones del chat: las piezas (image_id de creaciones; varias imágenes = carrusel, un video solo = reel), su texto y cuándo sale. NO publica en Instagram: queda planificado para que el usuario lo revise y lo suba, y se edita o borra desde el calendario. Decíselo así, sin prometer que sale solo.\n' +
    'Usala cuando el usuario pide mandar, agendar o programar algo para Instagram ("mandá esto a Instagram", "agendalo para el jueves"). No pide confirmación: el pedido ya es explícito. Si pide VARIOS posts o "armame el mes", no la llames en cadena: proponé el plan en una tabla y esperá el sí; para eso conviene el calendario (`/instagram`), que tiene sus propias herramientas.\n' +
    'Si no dijo cuándo, omití `cuando` y se toma el próximo horario libre. El texto lo escribís vos con la voz de la marca y el gancho en la primera línea, salvo que él lo haya dictado.\n' +
    'Devuelve el `post_id`, cuándo quedó y cuántas piezas se colgaron, y deja en tu mensaje un botón «Ver en el calendario». Contá en una línea qué armaste y para cuándo.',
  input_schema: {
    type: 'object',
    properties: {
      image_ids: {
        type: 'array',
        items: { type: 'string' },
        description: `Las piezas, en orden (la primera es la portada), hasta ${MAX_PIEZAS_POST}. image_id de creaciones de la marca.`,
      },
      texto: {
        type: 'string',
        description: `La descripción del post con sus hashtags, hasta ${MAX_TEXTO_POST} caracteres.`,
      },
      cuando: {
        type: 'string',
        description:
          'Fecha y hora LOCAL en "YYYY-MM-DD HH:mm" (ej. "2026-10-16 19:30"). Omitila si el usuario no dijo cuándo: se toma el próximo horario libre.',
      },
    },
    required: ['image_ids'],
  },
};

export interface DepsMandarAInstagram {
  ahora: Date;
  /** El calendario de la marca activa (lo crea si no existe). null si no se pudo. */
  calendario: () => Promise<{ id: string } | null>;
  /** Los posts ya agendados de la empresa, para no pisar horarios. */
  agenda: () => Promise<{ agenda: PostEnAgenda[]; error: string | null }>;
  sugerir: (p: { agenda: readonly PostEnAgenda[]; ahora: Date }) => Sugerencia;
  leerFechaYHora: (s: unknown) => Date | null;
  fechaParaElModelo: (d: Date) => string;
  crearPost: (calendarioId: string, cuando: Date) => Promise<{ id: string | null; error: string | null }>;
  guardarTexto: (postId: string, texto: string) => Promise<{ error: string | null }>;
  colgarPiezas: (
    postId: string,
    imageIds: string[],
  ) => Promise<{ colgadas: number; problema: string | null; cuales: string }>;
  /** Marca el post como armado por el agente en el calendario (marca de pestaña). */
  marcarDelAgente: (calendarioId: string, postId: string) => void;
  /** Para no dejar un post vacío si ninguna pieza se pudo colgar. */
  borrarPost: (postId: string) => Promise<{ error: string | null }>;
}

type Resultado = { __texto: string; __error?: true; post_id?: string; cuando?: string };

const error = (texto: string): Resultado => ({ __texto: texto, __error: true });

function idsDe(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const limpios = v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim());
  return [...new Set(limpios)];
}

export async function resolverMandarAInstagram(
  input: unknown,
  deps: DepsMandarAInstagram,
): Promise<Resultado> {
  const datos = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const piezas = idsDe(datos.image_ids);
  if (piezas.length === 0) {
    return error('Falta `image_ids`: las creaciones que van en el post (al menos una).');
  }
  if (piezas.length > MAX_PIEZAS_POST) {
    return error(`Un post admite hasta ${MAX_PIEZAS_POST} piezas; mandaste ${piezas.length}.`);
  }

  let cuando: Date;
  let porque = '';
  if (datos.cuando != null && String(datos.cuando).trim() !== '') {
    const leida = deps.leerFechaYHora(datos.cuando);
    if (!leida) return error('`cuando` tiene que ser "YYYY-MM-DD HH:mm" en hora local, o omitirse.');
    if (leida.getTime() < deps.ahora.getTime()) {
      return error(`${deps.fechaParaElModelo(leida)} ya pasó. Pedí una fecha futura o omití \`cuando\`.`);
    }
    cuando = leida;
  } else {
    const { agenda, error: alLeer } = await deps.agenda();
    if (alLeer) return error(`No se pudo leer el calendario para elegir el horario: ${alLeer}`);
    const s = deps.sugerir({ agenda, ahora: deps.ahora });
    cuando = s.cuando;
    porque = s.porque;
  }

  const calendario = await deps.calendario();
  if (!calendario) return error('No se pudo abrir el calendario de Instagram de la marca. Probá de nuevo.');

  const creado = await deps.crearPost(calendario.id, cuando);
  if (creado.error || !creado.id) {
    return error(`No se creó el post: ${creado.error ?? 'error desconocido'}.`);
  }
  const postId = creado.id;

  // Primero las piezas: si no entra ninguna (ids inventados o de otra empresa), el post no
  // se queda vacío en el calendario.
  const c = await deps.colgarPiezas(postId, piezas);
  if (c.colgadas === 0) {
    await deps.borrarPost(postId);
    return error(
      `No se armó el post: ninguna pieza se pudo colgar${c.problema ? ` (${c.problema})` : ''}. Revisá los image_id.`,
    );
  }
  deps.marcarDelAgente(calendario.id, postId);

  const avisos: string[] = [];
  if (c.problema) avisos.push(c.problema);
  const texto = typeof datos.texto === 'string' ? datos.texto.slice(0, MAX_TEXTO_POST) : '';
  if (texto.trim()) {
    const g = await deps.guardarTexto(postId, texto);
    if (g.error) avisos.push(`el texto no se guardó (${g.error})`);
  }

  const fecha = deps.fechaParaElModelo(cuando);
  const lineas = [
    `Post agregado al calendario de Instagram: post_id=${postId} · ${fecha}${porque ? ` (horario libre: ${porque})` : ''} · ${c.colgadas} pieza${c.colgadas === 1 ? '' : 's'}${c.cuales ? ` [${c.cuales}]` : ''}.`,
    texto.trim() ? '' : 'Quedó sin texto: ofrecé escribirlo.',
    avisos.length ? `Ojo: ${avisos.join('; ')}.` : '',
    'No se publicó: queda planificado. En tu mensaje quedó el botón «Ver en el calendario».',
  ].filter(Boolean);
  return { __texto: lineas.join('\n'), post_id: postId, cuando: fecha };
}
